import { useEffect, useRef, useCallback } from 'react';
import useAuthStore from '../stores/authStore';

/**
 * Hook for real-time MD device status updates via WebSocket.
 * Connects to /ws/md-status and calls the onUpdate callback
 * whenever a health result or status change is received.
 * 
 * @param {function} onUpdate - Called with each update message
 * @param {boolean} enabled - Whether to connect
 */
export function useMdStatusUpdates(onUpdate, enabled = true) {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const onUpdateRef = useRef(onUpdate);
  const { tenant } = useAuthStore();
  const tenantId = tenant?.id || localStorage.getItem('activeTenantId') || 'default';

  // Keep callback ref current without triggering reconnects
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);

  const connect = useCallback(() => {
    if (!enabled || wsRef.current?.readyState === WebSocket.OPEN) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/md-status?tenantId=${tenantId}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[MD-StatusWS] Connected');
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'CONNECTED' || data.type === 'PONG') return;
          if (onUpdateRef.current) onUpdateRef.current(data);
        } catch (e) {}
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (enabled) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {};
    } catch (e) {
      console.error('[MD-StatusWS] Failed to connect:', e);
    }
  }, [enabled, tenantId]);

  useEffect(() => {
    if (enabled) connect();
    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    };
  }, [enabled, connect]);

  // Keepalive ping
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'PING' }));
      }
    }, 25000);
    return () => clearInterval(interval);
  }, [enabled]);
}

export default useMdStatusUpdates;
